import Header from '../layouts/Header';
import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Table, Modal, Alert, Row, Col, Spinner } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';

const DormitoryVillages = () => {
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    village_code: '',
    village_name: '',
    village_gender: 'm',
    village_status: 'active',
    village_note: ''
  });

  // Fetch all villages
  const fetchVillages = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/dormitory/villages');
      if (response.data.success) {
        setVillages(response.data.data);
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลหมู่หอพักไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVillages();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      village_code: '',
      village_name: '',
      village_gender: 'm',
      village_status: 'active',
      village_note: ''
    });
    setIsEdit(false);
    setEditId(null);
  };

  const handleShowModal = (village = null) => {
    if (village) {
      setFormData(village);
      setIsEdit(true);
      setEditId(village.village_id);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');

      if (!formData.village_code || !formData.village_name || !formData.village_gender) {
        setError('กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน');
        return;
      }

      let response;
      if (isEdit) {
        response = await axiosInstance.put(`/api/dormitory/villages/${editId}`, formData);
      } else {
        response = await axiosInstance.post('/api/dormitory/villages', formData);
      }

      if (response.data.success) {
        setSuccess(isEdit ? 'อัปเดตหมู่หอพักเรียบร้อยแล้ว' : 'สร้างหมู่หอพักเรียบร้อยแล้ว');
        fetchVillages();
        handleCloseModal();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'บันทึกข้อมูลหมู่หอพักไม่สำเร็จ';
      setError(errorMsg);
      
      if (err.response?.data?.code === 'DUPLICATE_VILLAGE_CODE') {
        setError('รหัสหมู่หอพักนี้มีอยู่แล้ว กรุณาใช้รหัสอื่น');
      }
    }
  };

  const genderLabel = (gender) => {
    return gender === 'm' ? '👨 ชาย' : '👩 หญิง';
  };

  const statusLabel = (status) => {
    return status === 'active' ? (
      <span className="badge bg-success">ใช้งาน</span>
    ) : (
      <span className="badge bg-secondary">ปิดใช้งาน</span>
    );
  };

  const maleVillages = villages.filter((village) => village.village_gender === 'm');
  const femaleVillages = villages.filter((village) => village.village_gender === 'f');

  const renderVillageTable = (title, list, emptyText) => (
    <div className="mb-4">
      <h5 className="mb-3">{title}</h5>
      <Table striped bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th style={{ width: '10%' }}>รหัส</th>
            <th style={{ width: '24%' }}>ชื่อ</th>
            <th style={{ width: '16%' }}>สถานะ</th>
            <th style={{ width: '34%' }}>หมายเหตุ</th>
            <th style={{ width: '16%' }}>จัดการ</th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center py-4 text-muted">
                {emptyText}
              </td>
            </tr>
          ) : (
            list.map(village => (
              <tr key={village.village_id}>
                <td><strong>{village.village_code}</strong></td>
                <td>{village.village_name}</td>
                <td>{statusLabel(village.village_status)}</td>
                <td>{village.village_note || '-'}</td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleShowModal(village)}
                  >
                    แก้ไข
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );

  return (
    <React.Fragment>
      <Header />
    <main className="main main-app p-3 p-lg-4">
      <Container fluid className="px-0">
        <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>จัดการหมู่หอพัก</h2>
        <Button variant="primary" onClick={() => handleShowModal()}>
          + เพิ่มหมู่หอพัก
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">กำลังโหลด...</span>
          </Spinner>
        </div>
      ) : (
        villages.length === 0 ? (
          <Alert variant="info" className="mb-0">
            ยังไม่มีข้อมูลหมู่หอพัก กรุณาเพิ่มรายการแรกเพื่อเริ่มต้น
          </Alert>
        ) : (
          <Row>
            <Col lg={6}>
              {renderVillageTable('👨 หมู่หอพักชาย', maleVillages, 'ยังไม่มีหมู่หอพักชาย')}
            </Col>
            <Col lg={6}>
              {renderVillageTable('👩 หมู่หอพักหญิง', femaleVillages, 'ยังไม่มีหมู่หอพักหญิง')}
            </Col>
          </Row>
        )
      )}

      {/* Modal for Create/Edit */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? 'แก้ไขหมู่หอพัก' : 'เพิ่มหมู่หอพัก'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                รหัสหมู่หอพัก <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="village_code"
                value={formData.village_code}
                onChange={handleInputChange}
                placeholder="เช่น M1, M12 F2, F20 เป็นต้น"
                disabled={isEdit}
                maxLength="50"
              />
              <Form.Text className="text-muted">รหัสอ้างอิงที่ไม่ซ้ำกันของหมู่หอพัก</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                ชื่อหมู่หอพัก <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="village_name"
                value={formData.village_name}
                onChange={handleInputChange}
                placeholder="เช่น เขต 1, เขต 2, เขต 3 เป็นต้น"
                maxLength="255"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    เพศ <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="village_gender"
                    value={formData.village_gender}
                    onChange={handleInputChange}
                  >
                    <option value="m">👨 ชาย</option>
                    <option value="f">👩 หญิง</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>สถานะ</Form.Label>
                  <Form.Select
                    name="village_status"
                    value={formData.village_status}
                    onChange={handleInputChange}
                  >
                    <option value="active">ใช้งาน</option>
                    <option value="inactive">ปิดใช้งาน</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                as="textarea"
                name="village_note"
                value={formData.village_note}
                onChange={handleInputChange}
                rows={3}
                placeholder="รายละเอียดเพิ่มเติม..."
                maxLength="500"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {isEdit ? 'บันทึกการแก้ไข' : 'สร้างหมู่หอพัก'}
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </main>
  </React.Fragment>
  );
};

export default DormitoryVillages;
